import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { SpinLoader } from "../Vendor/ImportCandidates";
import logo from "../../images/ebench_logo.png";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { loginUser } from "../../Services/authService";
import setAuthorizationToken from "../../utils/setAutorization";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  SIGNIN_USER_ERROR,
  SIGNIN_USER_REQUEST,
  SIGNIN_USER_SUCCESS,
} from "../../Redux/constants/AuthConstant";
import "./SignIn.css";

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({ email: "", password: "", userType: "" });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { email, password, userType } = state;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...state,
        email: state.email?.trim()?.toLowerCase(),
        password: state.password?.trim(),
        zoneId: timeZone,
      };
      console.log({payload});
      dispatch({ type: SIGNIN_USER_REQUEST });

      const res = await loginUser(payload);
      const {
        isAccountVerified,
        token,
        candidateId,
        vendorId,
        userType,
        email,
        firstName,
        lastName,
        logo,
        profileImageUrl,
        adminId,
      } = res;

      if (isAccountVerified) {
        const userInfo = {
          firstName,
          lastName,
          email,
          usertype: userType,
          userid: candidateId,
          vendorid: vendorId,
          token,
          adminid: adminId,
          logo,
          adminProfile: profileImageUrl,
        };
        Object.entries(userInfo).forEach(([key, value]) => {
          const existing = localStorage.getItem(key);
          if (existing !== value) localStorage.setItem(key, value ?? "");
        });

        localStorage.setItem("user", JSON.stringify(res));

        setAuthorizationToken(token);

        dispatch({
          type: SIGNIN_USER_SUCCESS,
          payload: userInfo,
          msg: "Login successful",
        });

        const redirectPaths = {
          VENDOR: "/vendor_dashboard",
          ADMIN: "/admin",
        };
        navigate(redirectPaths[userType] || "/candidate_dashboard");
      } else {
        if (localStorage.getItem("email") !== email)
          localStorage.setItem("email", email ?? "");
        if (localStorage.getItem("userTy") !== userType)
          localStorage.setItem("userTy", userType ?? "");

        navigate(userType === "VENDOR" ? "/votp-verify" : "/otp-verify", {
          state: { email, userType, candidateId, vendorId, token, logIn: true },
        });
      }
    } catch (err) {
      console.error("Login error", err);
      dispatch({
        type: SIGNIN_USER_ERROR,
        msg: "Login failed",
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="signin-header">
        <Link to="/" className="signin-logo-link">
          <motion.img
            src={logo}
            alt="eBench Logo"
            className="signin-logo"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
        </Link>

        <Link to="/register" className="header-login-btn">
          Sign Up
        </Link>
      </header>

      <div className="signin-page">
        {/* ===== LEFT SECTION ===== */}
        <div className="signin-left">
          <div className="overlay"></div>
          <div className="signin-left-content">
            <div className="logo-box white-badge">
              <img src={logo} alt="eBench Logo" />
            </div>
            <h2>
              Welcome back to <span className="highlight">eBench</span>
            </h2>
            <p>
              Reconnect with your network, explore opportunities, and manage
              your professional growth. Let’s continue your journey together.
            </p>

            <ul className="signin-highlights">
              <li>🚀 Quick access to personalized dashboards</li>
              <li>🤝 Collaborate with vendors and clients seamlessly</li>
              <li>📊 Track progress with real-time analytics</li>
            </ul>
          </div>
        </div>

        {/* ===== RIGHT SECTION (FORM) ===== */}
        <div className="signin-right">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="signin-form-wrapper"
          >
            <div className="signin-card">
              <h1 className="signin-title">
                Welcome Back
                <div className="accent-bar"></div>
              </h1>

              <form onSubmit={handleSubmit}>
                {/* User Type */}
                {/* ===== User Type Selector ===== */}
                <div className="form-group user-type-group">
                  <label className="user-type-label">Select User Type</label>
                  <div className="radio-group-horizontal">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="userType"
                        value="CANDIDATE"
                        checked={userType === "CANDIDATE"}
                        onChange={handleInputChange}
                        required
                      />
                      <span>Candidate</span>
                    </label>

                    <label className="radio-option">
                      <input
                        type="radio"
                        name="userType"
                        value="VENDOR"
                        checked={userType === "VENDOR"}
                        onChange={handleInputChange}
                        required
                      />
                      <span>Vendor</span>
                    </label>
                  </div>
                </div>

                {/* Email */}
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {/* Password */}
                <div className="form-group password-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={handleInputChange}
                    required
                  />
                  <span
                    onClick={togglePasswordVisibility}
                    className="password-toggle"
                  >
                    {showPassword ? <IoMdEyeOff /> : <IoMdEye />}
                  </span>
                </div>

                <div className="forgot-link">
                  <Link to="/forgot-pass">Forgot Password?</Link>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="btn-primary"
                >
                  {!loading ? "Log In" : <SpinLoader />}
                </motion.button>
              </form>

              <p className="signup-redirect">
                Don’t have an account?{" "}
                <Link to="/register">Sign up to get started</Link>.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
